import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

export interface CheckoutCartItem {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  modifiers?: {
    questionId: string;
    answerId: string;
    answerText: string;
    priceAdjustment: number;
    parentAnswerId?: string;
  }[];
  notes?: string;
}

export interface CheckoutStartRequest {
  service: 'takeout' | 'delivery';
  timing: 'asap' | 'scheduled';
  scheduledTime?: number;
  scheduledStart?: number;
  scheduledEnd?: number;
  addressId?: number;
  phoneId?: number;
  tip?: number;
  items: CheckoutCartItem[];
}

interface CartItem {
  id: string;
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  modifiers?: {
    questionId: string;
    answerId: string;
    answerText: string;
    priceAdjustment: number;
  }[];
  notes?: string;
}

export interface CheckoutSummary {
  subtotal: number;
  tax: number;
  deliveryFee?: number;
  serviceFee?: number;
  tip: number;
  total: number;
}

export interface CheckoutFee {
  id: number;
  name: string;
  is_delivery_fee: boolean;
  service_fee_type_id: number;
  type: 'flat' | 'percent';
  base?: number;
  adjustable?: boolean;
  adjustable_percentages?: number[];
  percent?: number;
  amount: number;
}

export interface CheckoutTax {
  id: number;
  name: string;
  scope: 'item' | 'fee';
  targets: {
    type: string;
    id: number;
    sort?: number;
    base: number;
    percent: number;
    amount: number;
    total: number;
  }[];
}

export interface CheckoutResponse {
  restaurantId: string;
  valid: boolean;
  errors?: string[];
  summary: CheckoutSummary;
  estimatedTime?: string;
  items: CartItem[];
  checkout: string;
  client: string;
  fees: CheckoutFee[];
  taxes: CheckoutTax[];
  tipOptions?: number[];
  defaultTipPercent?: number;
}

interface CartSetResponse {
  success: boolean;
  restaurantId: string;
  error?: string;
  message?: string;
}

/**
 * Step 1: Set cart in Eatzy DB
 * This saves the cart to Eatzy's database before checkout
 */
export function useSetCart(restaurantId: string | undefined) {
  return useMutation({
    mutationFn: async (request: CheckoutStartRequest) => {
      if (!restaurantId) {
        throw new Error('Restaurant ID is required');
      }
      const response = await apiRequest(
        'PUT',
        `/api/restaurants/${restaurantId}/cart/set`,
        request
      );
      return response.json() as Promise<CartSetResponse>;
    },
  });
}

/**
 * Step 2: Start checkout process
 * This validates the cart and returns pricing/fees
 */
export function useStartCheckout(restaurantId: string | undefined) {
  return useMutation({
    mutationFn: async (request: CheckoutStartRequest) => {
      if (!restaurantId) {
        throw new Error('Restaurant ID is required');
      }
      const response = await apiRequest(
        'POST',
        `/api/restaurants/${restaurantId}/checkout/start`,
        request
      );
      return response.json() as Promise<CheckoutResponse>;
    },
  });
}

/**
 * Combined checkout flow: setCart -> startCheckout
 * This is the main hook used by the Checkout page
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (typeof window === "undefined") {
    return headers;
  }
  const token = window.localStorage.getItem("auth_token");
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function safeParseJson<T>(response: Response): Promise<{ data: T | null; error: string | null }> {
  try {
    const text = await response.text();
    if (!text) {
      return { data: null, error: response.ok ? null : `Server error: ${response.status}` };
    }
    const data = JSON.parse(text) as T;
    return { data, error: null };
  } catch (e) {
    return { data: null, error: `Unable to process server response (${response.status})` };
  }
}

export function useInitiateCheckout(restaurantId: string | undefined) {
  return useMutation({
    mutationFn: async (request: CheckoutStartRequest) => {
      if (!restaurantId) {
        throw new Error('Restaurant ID is required');
      }
      
      // Step 1: Set cart in Eatzy DB
      console.log('[Checkout] Step 1: Setting cart in Eatzy...', request);
      const setCartResponse = await fetch(`/api/restaurants/${restaurantId}/cart/set`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(request),
        credentials: 'include',
      });
      
      const { data: cartResult, error: cartParseError } = await safeParseJson<CartSetResponse>(setCartResponse);
      console.log('[Checkout] Cart set result:', cartResult);
      
      if (cartParseError) {
        console.error('[Checkout] Cart set parse error:', cartParseError);
        throw new Error(cartParseError);
      }
      
      if (!setCartResponse.ok || !cartResult?.success) {
        const errorMsg = cartResult?.error || cartResult?.message || 'Failed to save cart';
        console.error('[Checkout] Cart set failed:', errorMsg);
        throw new Error(errorMsg);
      }
      
      // Step 2: Start checkout to get pricing
      console.log('[Checkout] Step 2: Starting checkout...');
      const checkoutResponse = await fetch(`/api/restaurants/${restaurantId}/checkout/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(request),
        credentials: 'include',
      });
      
      const { data: checkoutResult, error: checkoutParseError } = await safeParseJson<CheckoutResponse>(checkoutResponse);
      console.log('[Checkout] Checkout result:', checkoutResult);
      
      if (checkoutParseError) {
        console.error('[Checkout] Checkout parse error:', checkoutParseError);
        throw new Error(checkoutParseError);
      }
      
      // Even if HTTP status is error, we now have the parsed JSON with user-friendly error message
      if (!checkoutResponse.ok) {
        // The backend returns {error: "user friendly message", message: "raw error", valid: false, errors: [...]}
        const errorMsg = (checkoutResult as any)?.error || (checkoutResult as any)?.message || 'Checkout failed';
        console.error('[Checkout] Checkout start failed:', errorMsg);
        throw new Error(errorMsg);
      }
      
      return checkoutResult!;
    },
  });
}
